import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { DEFAULT_LEASE_FORM, LeaseFormState, leaseToForm, renewFromLease, ordinal } from './leaseFormShared';
import { formatLegalOwner, findPropertyByAddress } from './propertyFormShared';

const CSS = `
.lf-page { min-height: 100vh; background: #fafafa; font-family: 'DM Sans', system-ui, sans-serif; padding-bottom: 88px; }
.lf-top { position: sticky; top: 0; z-index: 20; background: #fff; border-bottom: 1px solid #e5e7eb; padding: 16px 24px; }
.lf-back { background: none; border: none; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; padding: 0; margin-bottom: 8px; display: block; }
.lf-title { margin: 0; font-size: 22px; font-weight: 800; color: #111; }
.lf-sub { margin: 4px 0 0; font-size: 12px; color: #6b7280; }
.lf-body { max-width: 920px; margin: 0 auto; padding: 24px; }
.lf-section { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 20px 22px; margin-bottom: 16px; }
.lf-section-title { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #374151; margin: 0 0 16px; padding-bottom: 10px; border-bottom: 1px solid #f3f4f6; }
.lf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
.lf-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.lf-req { color: #16a34a; }
.lf-input, .lf-select { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; color: #111; background: #fff; box-sizing: border-box; font-family: inherit; }
.lf-input:focus, .lf-select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,0.12); }
.lf-footer { position: fixed; bottom: 0; left: 0; right: 0; z-index: 30; background: #fff; border-top: 1px solid #e5e7eb; padding: 14px 24px; display: flex; justify-content: flex-end; gap: 10px; box-shadow: 0 -4px 20px rgba(0,0,0,0.06); }
.lf-btn-cancel { padding: 11px 22px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #374151; font-weight: 700; font-size: 14px; cursor: pointer; }
.lf-btn-save { padding: 11px 28px; border: none; border-radius: 8px; background: #16a34a; color: #fff; font-weight: 800; font-size: 14px; cursor: pointer; }
.lf-btn-save:disabled { opacity: 0.6; cursor: default; }
.lf-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; font-weight: 600; }
.lf-hint { margin-top: 12px; padding: 10px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; font-size: 13px; color: #92400e; }
`;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="lf-section"><h2 className="lf-section-title">{title}</h2>{children}</div>;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return <div><label className="lf-label">{label}{required && <span className="lf-req"> *</span>}</label>{children}</div>;
}

export default function LeaseFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const renewFrom = searchParams.get('renew_from');
  const prefillTenantId = searchParams.get('tenant_id');
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const saveInFlight = useRef(false);
  const prefillApplied = useRef(false);

  const [form, setForm] = useState<LeaseFormState>({ ...DEFAULT_LEASE_FORM });
  const [properties, setProperties] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [propertyRooms, setPropertyRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(isEdit || !!renewFrom);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [renewalSourceId, setRenewalSourceId] = useState<number | null>(null);
  const [loadedLegalLandlord, setLoadedLegalLandlord] = useState('');
  const [loadedPropertyId, setLoadedPropertyId] = useState<number | null>(null);
  const [legalFromProperty, setLegalFromProperty] = useState(false);

  const set = <K extends keyof LeaseFormState>(key: K, val: LeaseFormState[K]) =>
    setForm(p => ({ ...p, [key]: val }));

  const loadRooms = useCallback(async (address: string) => {
    if (!address || !token) { setPropertyRooms([]); return; }
    try {
      const r = await fetch(`${API_URL}/landlord/rooms?property_address=${encodeURIComponent(address)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      const apiRooms = Array.isArray(data) ? data : [];
      if (apiRooms.length > 0) { setPropertyRooms(apiRooms); return; }
      const prop = properties.find((p: any) => p.address === address);
      const total = Number(prop?.bedrooms || 0);
      if (total > 0) {
        setPropertyRooms(Array.from({ length: total }, (_, i) => ({ id: `v-${i}`, room_name: `Room ${i + 1}`, room_number: String(i + 1) })));
      } else setPropertyRooms([]);
    } catch { setPropertyRooms([]); }
  }, [token, properties]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/tenants`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([p, t]) => {
      setProperties((p.properties || []).filter((x: any) => !x.archived));
      setTenants((t.tenants || []).filter((x: any) => !x.archived));
    });
    if (!isEdit) {
      fetch(`${API_URL}/landlord/preferences`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const prefs = d.preferences || {};
          setForm(f => ({
            ...f,
            payment_day: String(prefs.default_payment_day || 1),
            notice_period: String(prefs.default_notice_period || 2),
            tenancy_type: prefs.default_tenancy_type || 'AST',
            deposit_scheme: prefs.default_deposit_scheme || '',
            late_fee_amount: prefs.default_late_fee_amount != null ? String(prefs.default_late_fee_amount) : '',
            late_fee_grace_days: String(prefs.default_late_fee_grace_days || 3),
          }));
        })
        .catch(() => {});
    }
  }, [token, isEdit]);

  useEffect(() => {
    if (!token || !isEdit || !id) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/landlord/leases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Not found');
        setForm(leaseToForm(d.lease));
        setLoadedLegalLandlord(d.lease.legal_landlord || '');
        setLoadedPropertyId(d.lease.property_id ?? null);
        setLegalFromProperty(!!d.lease.legal_landlord_from_property);
        loadRooms(d.lease.property_address);
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, [token, isEdit, id, loadRooms]);

  useEffect(() => {
    if (!token || isEdit || !renewFrom) return;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/landlord/leases/${renewFrom}`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Not found');
        setForm(renewFromLease(d.lease));
        setRenewalSourceId(Number(renewFrom));
        setLoadedLegalLandlord(d.lease.legal_landlord || '');
        setLoadedPropertyId(d.lease.property_id ?? null);
        setLegalFromProperty(!!d.lease.legal_landlord_from_property);
        loadRooms(d.lease.property_address);
      } catch (e: any) {
        setError(e.message || 'Failed to load renewal');
      } finally { setLoading(false); }
    })();
  }, [token, isEdit, renewFrom, loadRooms]);

  useEffect(() => {
    if (form.property_address) loadRooms(form.property_address);
  }, [form.property_address, loadRooms]);

  useEffect(() => {
    if (isEdit || renewFrom || prefillApplied.current) return;
    const state = location.state as { tenantId?: number; tenantName?: string; propertyAddress?: string } | null;
    const tenantId = state?.tenantId ?? (prefillTenantId ? Number(prefillTenantId) : null);
    if (!tenantId && !state?.tenantName) return;

    const tenant = tenantId
      ? tenants.find((t: any) => t.id === tenantId)
      : tenants.find((t: any) => t.full_name === state?.tenantName);
    const propertyAddress = tenant?.property_address || state?.propertyAddress || '';

    if (tenant || state?.tenantName) {
      setForm(f => ({
        ...f,
        tenant_name: tenant?.full_name || state?.tenantName || f.tenant_name,
        property_address: propertyAddress || f.property_address,
        room_number: tenant?.room_number || f.room_number,
      }));
      if (propertyAddress) loadRooms(propertyAddress);
      prefillApplied.current = true;
    }
  }, [isEdit, renewFrom, tenants, location.state, prefillTenantId, loadRooms]);

  const save = async () => {
    if (saveInFlight.current) return;
    if (!form.property_address || !form.tenant_name || !form.start_date || !form.end_date || !form.monthly_rent) {
      setError('Property, tenant, dates and monthly rent are required');
      return;
    }
    saveInFlight.current = true;
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      monthly_rent: Number(form.monthly_rent),
      deposit: Number(form.deposit) || 0,
      payment_day: Number(form.payment_day) || 1,
      notice_period: Number(form.notice_period) || 2,
    };
    try {
      const url = isEdit ? `${API_URL}/landlord/leases/${id}` : `${API_URL}/landlord/leases`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to save'); setSaving(false); saveInFlight.current = false; return; }
      showToast(isEdit ? 'Tenancy updated' : renewalSourceId ? 'Renewal tenancy created' : 'Tenancy created');
      if (!isEdit) {
        const leaseId = d.lease?.id;
        const tenantId = d.tenant_id || d.lease?.tenant_id;
        if (tenantId && window.confirm('Lease created! Send a tenant portal invite now?')) {
          try {
            const inv = await fetch(`${API_URL}/landlord/tenants/${tenantId}/invite-portal`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            });
            const invData = await inv.json();
            if (inv.ok) showToast('Portal invite sent');
            else showToast(invData.error || 'Could not send invite');
          } catch {
            showToast('Could not send portal invite');
          }
        }
        navigate(leaseId ? `/landlord/leases/${leaseId}` : '/landlord/leases');
      } else {
        navigate('/landlord/leases');
      }
    } catch {
      setError('Network error');
    }
    setSaving(false);
    saveInFlight.current = false;
  };

  if (loading) {
    return <div className="lf-page"><style>{CSS}</style><div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>Loading…</div></div>;
  }

  const selectedProperty = findPropertyByAddress(properties, form.property_address);
  const legalLandlord = formatLegalOwner(selectedProperty?.legal_owner_title, selectedProperty?.legal_owner_name)
    || loadedLegalLandlord;
  const propertyId = selectedProperty?.id ?? loadedPropertyId;
  const hasPropertyLegal = !!(legalLandlord && (legalFromProperty || formatLegalOwner(selectedProperty?.legal_owner_title, selectedProperty?.legal_owner_name)));

  return (
    <div className="lf-page">
      <style>{CSS}</style>
      <div className="lf-top">
        <button type="button" className="lf-back" onClick={() => navigate('/landlord/leases')}>← Back to Leases</button>
        <h1 className="lf-title">{isEdit ? 'Edit Tenancy' : renewalSourceId ? `Renew Tenancy — ${form.tenant_name}` : 'New Tenancy'}</h1>
        <p className="lf-sub">{isEdit ? `Lease #${id}` : renewalSourceId ? `Linked to lease #${renewalSourceId}` : 'Create an assured shorthold tenancy agreement'}</p>
      </div>
      <div className="lf-body">
        {error && <div className="lf-error">{error}</div>}
        {!isEdit && (
          <div className="lf-hint" style={{ marginBottom: 16 }}>
            <strong>Tenancy agreement summary</strong> — ShahoSuite generates a record-keeping summary, not a solicitor-drafted AST. Seek independent legal advice for formal tenancy agreements.
          </div>
        )}

        <Section title="Property & Tenant">
          <div className="lf-grid">
            <Field label="Property" required>
              <select className="lf-select" value={form.property_address} onChange={e => {
                const addr = e.target.value;
                const linked = tenants.find((t: any) => t.property_address === addr);
                setForm(p => ({ ...p, property_address: addr, tenant_name: linked && !isEdit ? linked.full_name : p.tenant_name, room_number: '' }));
              }}>
                <option value="">— Select property —</option>
                {properties.map((p: any) => <option key={p.id} value={p.address}>{p.address}, {p.postcode}</option>)}
              </select>
            </Field>
            <Field label="Tenant" required>
              <select className="lf-select" value={form.tenant_name} onChange={e => set('tenant_name', e.target.value)}>
                <option value="">— Select tenant —</option>
                {form.property_address && tenants.filter((t: any) => t.property_address === form.property_address).map((t: any) => (
                  <option key={`l-${t.id}`} value={t.full_name}>{t.full_name}</option>
                ))}
                {tenants.filter((t: any) => t.property_address !== form.property_address).map((t: any) => (
                  <option key={t.id} value={t.full_name}>{t.full_name}</option>
                ))}
              </select>
            </Field>
            <Field label="Tenancy Type" required>
              <select className="lf-select" value={form.tenancy_type} onChange={e => set('tenancy_type', e.target.value)}>
                <option value="AST">AST</option>
                <option value="Periodic">Periodic</option>
                <option value="Fixed">Fixed Term</option>
                <option value="HMO">HMO Licence</option>
              </select>
            </Field>
            <Field label="Room / Unit">
              {propertyRooms.length > 0 ? (
                <select className="lf-select" value={form.room_number} onChange={e => set('room_number', e.target.value)}>
                  <option value="">— Whole property —</option>
                  {propertyRooms.map((r: any) => <option key={r.id} value={r.room_number || r.room_name}>{r.room_name}</option>)}
                </select>
              ) : (
                <input className="lf-input" value={form.room_number} onChange={e => set('room_number', e.target.value)} placeholder="Room 1, Flat 2…" />
              )}
            </Field>
          </div>
        </Section>

        {form.property_address && (
          <Section title="Legal Landlord (for AST & contracts)">
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              Pulled from the linked property — this name appears on the tenancy agreement PDF and tenant portal.
            </p>
            <div style={{ padding: '14px 16px', background: hasPropertyLegal ? '#f0fdf4' : '#fef3c7', border: `1px solid ${hasPropertyLegal ? '#bbf7d0' : '#fde68a'}`, borderRadius: 8, fontSize: 15, fontWeight: 700, color: hasPropertyLegal ? '#166534' : '#92400e' }}>
              {legalLandlord || 'Not set on property yet'}
            </div>
            {propertyId && (
              <button type="button" onClick={() => navigate(`/landlord/properties/${propertyId}/edit`)} style={{ marginTop: 12, background: 'none', border: 'none', color: '#012169', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 13, textDecoration: 'underline' }}>
                {hasPropertyLegal ? 'Edit legal landlord on property' : 'Set legal landlord on property'} →
              </button>
            )}
          </Section>
        )}

        <Section title="Term & Rent">
          <div className="lf-grid">
            <Field label="Start Date" required><input type="date" className="lf-input" value={form.start_date} onChange={e => set('start_date', e.target.value)} /></Field>
            <Field label="End Date" required><input type="date" className="lf-input" value={form.end_date} onChange={e => set('end_date', e.target.value)} /></Field>
            <Field label="Monthly Rent (£)" required><input type="number" className="lf-input" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} /></Field>
            <Field label="Security Deposit (£)"><input type="number" className="lf-input" value={form.deposit} onChange={e => set('deposit', e.target.value)} /></Field>
            <Field label="Notice Period">
              <select className="lf-select" value={form.notice_period} onChange={e => set('notice_period', e.target.value)}>
                {[1, 2, 3, 6].map(n => <option key={n} value={String(n)}>{n} month{n > 1 ? 's' : ''}</option>)}
              </select>
            </Field>
            <Field label="Rent Due Day">
              <select className="lf-select" value={form.payment_day} onChange={e => set('payment_day', e.target.value)}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={String(d)}>{ordinal(d)} of month</option>)}
              </select>
            </Field>
            <Field label="Renewal Date (optional)"><input type="date" className="lf-input" value={form.renewal_date} onChange={e => set('renewal_date', e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Deposit Protection">
          <div className="lf-grid">
            <Field label="Scheme">
              <select className="lf-select" value={form.deposit_scheme} onChange={e => set('deposit_scheme', e.target.value)}>
                <option value="">Not registered</option>
                <option value="TDS">TDS</option>
                <option value="DPS">DPS</option>
                <option value="MyDeposits">MyDeposits</option>
                <option value="None">No deposit taken</option>
              </select>
            </Field>
            <Field label="Registration Date"><input type="date" className="lf-input" value={form.deposit_registered_date} onChange={e => set('deposit_registered_date', e.target.value)} /></Field>
            <Field label="Certificate / Ref"><input className="lf-input" value={form.deposit_ref} onChange={e => set('deposit_ref', e.target.value)} placeholder="TDS-123456" /></Field>
          </div>
        </Section>

        <Section title="Break Clause & Late Fees">
          <div className="lf-grid">
            <Field label="Break Clause (months)"><input type="number" className="lf-input" value={form.break_clause_months} onChange={e => set('break_clause_months', e.target.value)} placeholder="6" /></Field>
            <Field label="Late Fee (£)"><input type="number" className="lf-input" value={form.late_fee_amount} onChange={e => set('late_fee_amount', e.target.value)} /></Field>
            <Field label="Grace Days"><input type="number" className="lf-input" value={form.late_fee_grace_days} onChange={e => set('late_fee_grace_days', e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Notes">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={form.agreement_signed} onChange={e => set('agreement_signed', e.target.checked)} style={{ width: 18, height: 18, accentColor: '#16a34a' }} />
            Agreement signed by tenant
          </label>
          <textarea className="lf-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any extra notes…" style={{ resize: 'vertical' }} />
          {!isEdit && <div className="lf-hint">Rent records are generated automatically for each month of the lease term.</div>}
        </Section>
      </div>

      <div className="lf-footer">
        <button type="button" className="lf-btn-cancel" onClick={() => navigate('/landlord/leases')}>Cancel</button>
        <button type="button" className="lf-btn-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Tenancy'}</button>
      </div>
    </div>
  );
}
